import React, { useState, useEffect, forwardRef } from 'react'
import PropTypes from 'prop-types'
import { formatAndSanitizeHTML } from 'utils/misc'
import { Button } from 'components/UIElements'
import SurveyQuestion from '../SurveyQuestion'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import {
  checkLogicAndComplete,
  getShouldRenderNextQuestion,
  parseVASValue,
} from '../../utils/instrumentUtils'

const persistVAScaleSubmission = (props, value) => {
  try {
    const { question, id, webSurvey, completeQuestion } = props
    if (typeof completeQuestion !== 'function' || !webSurvey) return
    const { label, type, attributes } = question
    const { required } = attributes
    const answer = value
      ? [
        {
          label,
          value,
          question: id,
        },
      ]
      : []
    const submission = {
      id,
      label,
      name: id,
      type,
      required,
      state: value ? 'ANSWERED' : 'SKIPPED',
      answer,
    }
    completeQuestion(submission, webSurvey.order?.[0])
  } catch (e) {
    console.warn('VAScale autosave failed', { error: e })
  }
}

const completeVAScaleQuestion = (props, value) => {
  const {
    question,
    id,
    completeQuestion,
    nextQ,
    webSurvey,
    submissionSurvey,
    nextQId,
    reviewFunctionalitySupported,
    isConsent = false,
    isLastQuestion = false,
    isSibling = false,
  } = props
  const { label, type, attributes } = question
  const { required } = attributes
  const answer = value
    ? [
      {
        label,
        value,
        question: id,
      },
    ]
    : []
  const submission = {
    id,
    label,
    name: id,
    type,
    required,
    state: value ? 'ANSWERED' : 'SKIPPED',
    answer
  }
  const currentAnswer = { [id]: answer }

  const shouldRenderNextQ = getShouldRenderNextQuestion({
    isConsent,
    isLastQuestion,
    isSibling,
  })

  checkLogicAndComplete(
    webSurvey,
    id,
    submissionSurvey,
    currentAnswer,
    nextQId,
    nextQ,
    completeQuestion,
    submission,
    reviewFunctionalitySupported,
    shouldRenderNextQ,
  )
}

const SpecifyValue = ({ onSelect, strings, value }) => {
  const onIncrement = () => {
    if (Number(value) < 100) {
      onSelect((Number(value) + 1).toString())
    }
  }
  const onDecrement = () => {
    if (Number(value) > 0) {
      onSelect((Number(value) - 1).toString())
    }
  }

  return (
    <div
      className={`specify-value-container flexed center-justified column ${value ? 'highlighted' : ''
        }`}>
      {value && (
        <>
          <Button icon='fas fa-caret-up' onClick={(val) => onIncrement(val)} />
          <div className='value flexed center-justified'>
            <input
              type='number'
              onChange={(evt) => {
                evt.preventDefault()
                onSelect(parseVASValue(evt.target.value, value))
              }}
              value={value}
            />
          </div>
          <Button
            icon='fas fa-caret-down'
            onClick={(val) => onDecrement(val)}
          />
        </>
      )}
      <span>
        {value ? strings.vas_scale_specify_value : strings.vas_scale_click_on}
      </span>
    </div>
  )
}

const Scale = ({ bottomLabel, topLabel, onSelect, strings, value }) => {
  const [dragStarted, setDragStarted] = useState(false)
  const numDivisions = [...Array(10).keys()]
  const numShortLines = [...Array(4).keys()]

  const handleStyle = {
    // top: 445px will position handles at "0"
    top: `${445 - 4.5 * value}px`
  }

  return (
    <div className='scale-container'>
      <strong className='top'>100</strong>
      <strong className='top right'>{topLabel}</strong>
      <strong className='bottom left'>0</strong>
      <strong className='bottom right'>{bottomLabel}</strong>
      {value && <div className='handle' style={handleStyle} />}
      {value && <div className='handle right' style={handleStyle} />}
      {numDivisions.map((idx) => {
        const shortLines = numShortLines.map((n) => {
          return <div className='line short' key={n} />
        })
        return (
          <div className='scale flexed column' key={idx}>
            <div className='line' />
            {shortLines}
            <div className='line medium' />
            {shortLines}
            {idx === 9 && <div className='line' />}
          </div>
        )
      })}
      <input
        type='range'
        onChange={(evt) => {
          if (!dragStarted) {
            setDragStarted(true)
            onSelect(evt.target.value)
          }
        }}
        onMouseUp={() => setDragStarted(false)}
        onTouchEnd={() => setDragStarted(false)}
        value={value}
      />
      <SpecifyValue onSelect={onSelect} strings={strings} value={value} />
    </div>
  )
}

const VAScale = forwardRef((props, ref) => {
  const [selectedValue, setSelectedValue] = useState('')

  const {
    className,
    id,
    isConsent,
    isLastQuestion,
    question,
    renderBackButton,
    setAllRequiredAnswered,
    strings,
    storedAnswer,
    submissionLoading,
    submissionSurvey,
    webSurvey,
    isSibling = false,
  } = props
  const { label, attributes, scale_labels: scaleLabels } = question
  const { required } = attributes
  const { top, bottom } = scaleLabels
  const _className = className ? ` ${className}` : ''

  useEffect(() => {
    if (storedAnswer?.[0]?.value) {
      const storedValue = storedAnswer[0].value
      setAllRequiredAnswered(false, id)
      setSelectedValue(storedValue)
      if (!props.suppressAutoAdvance) {
        completeVAScaleQuestion(props, storedValue)
      } else {
        persistVAScaleSubmission(props, storedValue)
      }
    }
  }, [])

  const onSelect = (val) => {
    setAllRequiredAnswered(false, id)
    setSelectedValue(val)
    persistVAScaleSubmission(props, val)
  }

  return (
    <SurveyQuestion id={id}>
      <MakeScrollable>
        <div ref={ref} className={`survey-question va-scale${_className}`}>
          <p className='question-label'>
            <span
              dangerouslySetInnerHTML={{
                __html: formatAndSanitizeHTML(label)
              }}
            />
          </p>
          <Scale
            topLabel={top}
            bottomLabel={bottom}
            onSelect={onSelect}
            strings={strings}
            value={selectedValue}
          />
          {!isSibling && (
            <QuestionFooter
              completed={!!selectedValue}
              id={id}
              isConsent={isConsent}
              isLastQuestion={isLastQuestion}
              loading={submissionLoading}
              onComplete={() => completeVAScaleQuestion(props, selectedValue)}
              question={question}
              renderBackButton={renderBackButton}
              strings={strings}
              submissionSurvey={submissionSurvey}
              webSurvey={webSurvey}
            />
          )}
        </div>
      </MakeScrollable>
    </SurveyQuestion>
  )
})

export default VAScale

VAScale.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string,
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  setAllRequiredAnswered: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string)
}

Scale.propTypes = {
  bottomLabel: PropTypes.string,
  topLabel: PropTypes.string,
  onSelect: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  value: PropTypes.string
}

SpecifyValue.propTypes = Scale.propTypes
